/**
 * AI 평가 구성 결과(작은 스키마) → 앱 데이터 구조 변환.
 * - 기준안: 기본 템플릿 + 마커(isGubun/isCriterion/hidden) 그대로 쓰고 칸만 채움 → 편집기·보기·엑셀 호환
 * - 평가지: 섹션을 A4(794px 기준) 헤더 아래 위→아래로 배치 (텍스트·표·도형/선·소스코드 블럭)
 */
import type { EvaluationPlanDraft, EvaluationLevelDisplayLabels, EvaluationLevelStepCount } from '../types/evaluationPlan';
import { getActiveLevelLabels } from '../types/evaluationPlan';
import {
  addWebDocumentColumnAfter,
  applyEvaluationPlanToWebDocument,
  buildWebDocumentFromTemplateStructure,
  getExampleAnswerTitle,
  getWebDocumentDualLabelLines,
  webDocumentColumnDisplayHeader,
  type EvaluationPlanWebDocumentTemplateStructure,
  createDefaultEvaluationPlanWebDocument,
  getWebDocumentCriterionColumnId,
  isWebDocumentGubunColumn,
  type EvaluationPlanWebDocumentData,
  type WebDocumentPlanApplyStandard,
} from '../types/evaluationPlanWebDocument';
import {
  createDefaultEvaluationPlanSheetCanvasDocument,
  createEmptySheetLayoutSubset,
  withSheetCanvasPages,
  EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM,
  EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
  type EvaluationPlanSheetCanvasDocumentV1,
} from '../types/evaluationPlanSheetCanvas';
import type {
  ProgramSlidePolylineBlockV1,
  ProgramSlideShapeBlockV1,
  ProgramSlideTableBlockV1,
  ProgramSlideTableCellV1,
  ProgramSlideTextBoxV1,
  ProgramSlideWebpageBlockV1,
} from '../types/programContentSlideCanvas';
import type { AiEvalRubricResult, AiEvalSheetResult, AiEvalSheetSection } from './aiEvaluationCompose';
import { SHEET_CODE_DESIGN_PAGE_WIDTH_PX, sanitizeSheetCodeHtml } from './sheetCodeHtmlSanitize';

const newId = () => crypto.randomUUID();

/* ───────────── 기준안 ───────────── */

/** '▪[관찰][구술] … ▪[서술형] …' → ['관찰','구술','서술형'] (대괄호 태그만, 중복 제거) */
export function parseMethodTags(methodText: string): string[] {
  const out: string[] = [];
  const re = /\[([^\[\]]{1,20})\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(methodText ?? '')))) {
    const tag = m[1].replace(/\s+/g, ' ').trim();
    if (tag && !out.includes(tag)) out.push(tag);
  }
  return out;
}

/** 수업·평가 방법 → 태그 뺀 활동 설명 줄들 */
export function parseMethodActivities(methodText: string): string[] {
  return String(methodText ?? '')
    .split(/\n|(?=▪)/)
    .map((l) => l.replace(/^[▪·\-\s]+/, '').replace(/\[[^\[\]]{1,20}\]\s*/g, '').trim())
    .filter(Boolean);
}

const squashText = (t: string) => String(t ?? '').replace(/[\s.,·▪\-]/g, '');

export function buildRubricWebDocumentFromAi(params: {
  plan: EvaluationPlanDraft;
  standards: WebDocumentPlanApplyStandard[];
  levelStepCount: EvaluationLevelStepCount;
  levelDisplayLabels?: Partial<EvaluationLevelDisplayLabels> | null;
  rubric: AiEvalRubricResult;
}): EvaluationPlanWebDocumentData {
  const { plan, standards, levelStepCount, levelDisplayLabels, rubric } = params;
  let doc = createDefaultEvaluationPlanWebDocument('criterion', levelStepCount, levelDisplayLabels);
  doc = applyEvaluationPlanToWebDocument(
    doc,
    {
      unit_text: plan.unit_text ?? '',
      evaluation_element_text: plan.evaluation_element_text ?? '',
      evaluation_method_text: plan.evaluation_method_text ?? '',
      standard_ids: plan.standard_ids ?? [],
      level_a: plan.level_a ?? '',
      level_b: plan.level_b ?? '',
      level_c: plan.level_c ?? '',
      level_d: plan.level_d ?? '',
      level_e: plan.level_e ?? '',
    },
    standards,
    levelStepCount,
    levelDisplayLabels
  );

  const items = rubric.items.length ? rubric.items : [{ header: '', cells: [] as string[] }];
  const criterionId = getWebDocumentCriterionColumnId(doc);
  const columnIds: string[] = [];
  if (criterionId) {
    columnIds.push(criterionId);
    doc = { ...doc, columns: doc.columns.map((c) => (c.id === criterionId ? { ...c, header: items[0].header || c.header } : c)) };
    let after = criterionId;
    for (let i = 1; i < items.length; i += 1) {
      const before = new Set(doc.columns.map((c) => c.id));
      doc = addWebDocumentColumnAfter(doc, after, items[i].header || undefined);
      const added = doc.columns.find((c) => !before.has(c.id));
      if (!added) break;
      columnIds.push(added.id);
      after = added.id;
    }
  }

  const levelLabels = getActiveLevelLabels(levelStepCount, levelDisplayLabels);
  // 평가 방법 칸: 태그 이름만 / 활동 설명은 평가 과제로
  const methodText = plan.evaluation_method_text ?? '';
  const tags = (rubric.method_tags?.length ? rubric.method_tags : parseMethodTags(methodText)).filter(Boolean);
  const activities = (rubric.task_activities?.length ? rubric.task_activities : parseMethodActivities(methodText)).filter(Boolean);
  const taskSquashed = squashText(rubric.task);
  const newActivities = activities.filter((a) => {
    const sq = squashText(a);
    return sq && !taskSquashed.includes(sq.slice(0, Math.min(sq.length, 24)));
  });
  const taskWithActivities = [
    newActivities.length ? newActivities.map((a) => `▪ ${a}`).join('\n') : '',
    rubric.task,
  ].filter((t) => t.trim()).join('\n\n');
  doc = {
    ...doc,
    rows: doc.rows.map((row) => {
      const idx = levelLabels.indexOf(row.label.trim());
      if (idx < 0) return row;
      const cells = { ...row.cells };
      columnIds.forEach((colId, ci) => {
        const v = items[ci]?.cells?.[idx];
        if (typeof v === 'string' && v.trim()) cells[colId] = v;
      });
      return { ...row, cells, memo: rubric.level_memos[idx] || row.memo || '' };
    }),
    exampleAnswer: {
      ...doc.exampleAnswer,
      title: rubric.task_title?.trim() || undefined,
      example: [
        taskWithActivities,
        rubric.example_answer_top?.trim() ? `\n[예시 답안 — 최상위 수준]\n${rubric.example_answer_top.trim()}` : '',
        rubric.example_answer_mid?.trim() ? `\n[예시 답안 — 보통 수준]\n${rubric.example_answer_mid.trim()}` : '',
      ].filter(Boolean).join('\n'),
      answer: '',
    },
    metaRows: doc.metaRows.map((row) => {
      if (row.layout === 'dual_label' && !row.isUserAdded) return { ...row, value: rubric.notice || row.value, hidden: false };
      const label = row.label.trim();
      if (!row.isUserAdded && (label === '평가 영역' || label === '영역') && tags.length) {
        return { ...row, pairValue: tags.join(', ') };
      }
      return row;
    }),
  };
  // 구분 열이 아닌 열에 대해 셀 키 보장
  const dataCols = doc.columns.filter((c) => !isWebDocumentGubunColumn(c)).map((c) => c.id);
  doc = { ...doc, rows: doc.rows.map((r) => ({ ...r, cells: Object.fromEntries(dataCols.map((id) => [id, r.cells[id] ?? ''])) })) };
  return doc;
}

/* ───────────── 사용자 기준안 양식 채우기 ───────────── */

export type RubricTemplateField = {
  /** 위치 기반 id: m{메타행} / mp{메타행}(오른쪽 칸) / c{행}_{열} / ex(평가 과제 본문) */
  id: string;
  /** 요소 타이틀(라벨·열 제목 × 행 라벨) */
  title: string;
  /** meta | cell | level_cell | task | notice */
  kind: string;
};

type RubricTemplateParams = {
  plan: EvaluationPlanDraft;
  standards: WebDocumentPlanApplyStandard[];
  levelStepCount: EvaluationLevelStepCount;
  levelDisplayLabels?: Partial<EvaluationLevelDisplayLabels> | null;
  structure: EvaluationPlanWebDocumentTemplateStructure;
};

/** 양식 복사본 + 평가계획(성취기준·단원 등) 자동 반영 — 위치(인덱스)가 항상 같게 결정적으로 만듦 */
function templateDocWithPlan(p: RubricTemplateParams): EvaluationPlanWebDocumentData {
  const base = buildWebDocumentFromTemplateStructure('criterion', p.structure);
  return applyEvaluationPlanToWebDocument(
    base,
    {
      unit_text: p.plan.unit_text ?? '',
      evaluation_element_text: p.plan.evaluation_element_text ?? '',
      evaluation_method_text: p.plan.evaluation_method_text ?? '',
      standard_ids: p.plan.standard_ids ?? [],
      level_a: p.plan.level_a ?? '',
      level_b: p.plan.level_b ?? '',
      level_c: p.plan.level_c ?? '',
      level_d: p.plan.level_d ?? '',
      level_e: p.plan.level_e ?? '',
    },
    p.standards,
    p.levelStepCount,
    p.levelDisplayLabels
  );
}

const oneLine = (t: string) => String(t ?? '').replace(/\s+/g, ' ').trim();

/** 양식에서 AI가 채울 빈 내용칸과 그 요소 타이틀을 모읍니다(라벨·이미 채워진 칸·읽기 전용·숨김 칸 제외). */
export function collectRubricTemplateFields(p: RubricTemplateParams): RubricTemplateField[] {
  const doc = templateDocWithPlan(p);
  const levelLabels = getActiveLevelLabels(p.levelStepCount, p.levelDisplayLabels);
  const fields: RubricTemplateField[] = [];
  doc.metaRows.forEach((row, i) => {
    if (row.readOnly || row.hidden) return;
    if (row.layout === 'dual_label') {
      if (!row.value.trim()) fields.push({ id: `m${i}`, title: oneLine(getWebDocumentDualLabelLines(row).join(' ')), kind: 'notice' });
      return;
    }
    if (!row.value.trim()) fields.push({ id: `m${i}`, title: oneLine(row.label) || '내용', kind: 'meta' });
    if (row.layout === 'pair' && row.pairLabel?.trim() && !row.pairValue?.trim()) {
      fields.push({ id: `mp${i}`, title: oneLine(row.pairLabel), kind: 'meta' });
    }
  });
  const dataCols = doc.columns.filter((c) => !isWebDocumentGubunColumn(c));
  const gubun = doc.columns.find((c) => isWebDocumentGubunColumn(c));
  const gubunTitle = gubun ? oneLine(webDocumentColumnDisplayHeader(gubun)) : '';
  doc.rows.forEach((row, ri) => {
    const isLevel = levelLabels.includes(row.label.trim());
    dataCols.forEach((col) => {
      const ci = doc.columns.indexOf(col);
      if ((row.cells[col.id] ?? '').trim()) return;
      const rowTitle = [gubunTitle, oneLine(row.label)].filter(Boolean).join(': ');
      fields.push({
        id: `c${ri}_${ci}`,
        title: `${oneLine(webDocumentColumnDisplayHeader(col)) || '항목'} × ${rowTitle || `${ri + 1}행`}`,
        kind: isLevel ? 'level_cell' : 'cell',
      });
    });
  });
  if (!doc.exampleAnswer.example.trim()) fields.push({ id: 'ex', title: oneLine(getExampleAnswerTitle(doc)), kind: 'task' });
  return fields.slice(0, 80);
}

/** 사용자 양식을 그대로 두고 내용칸만 AI 결과로 채웁니다. 채울 수 없는 칸은 비워 둠. */
export function buildRubricWebDocumentFromTemplate(p: RubricTemplateParams & { rubric: AiEvalRubricResult }): EvaluationPlanWebDocumentData {
  let doc = templateDocWithPlan(p);
  const values = new Map(
    (p.rubric.template_values ?? []).filter((v) => v && typeof v.id === 'string').map((v) => [v.id, String(v.value ?? '')])
  );
  const fieldIds = new Set(collectRubricTemplateFields(p).map((f) => f.id));
  const pick = (id: string, fallback = '') => (fieldIds.has(id) ? (values.get(id)?.trim() || fallback) : '');
  doc = {
    ...doc,
    metaRows: doc.metaRows.map((row, i) => {
      if (row.readOnly || row.hidden) return row;
      if (row.layout === 'dual_label') {
        const v = pick(`m${i}`, p.rubric.notice ?? '');
        return v ? { ...row, value: v } : row;
      }
      let next = row;
      const v = pick(`m${i}`);
      if (v) next = { ...next, value: v };
      const pv = pick(`mp${i}`);
      if (pv) next = { ...next, pairValue: pv };
      return next;
    }),
    rows: doc.rows.map((row, ri) => {
      const cells = { ...row.cells };
      doc.columns.forEach((col, ci) => {
        if (isWebDocumentGubunColumn(col)) return;
        const v = pick(`c${ri}_${ci}`);
        if (v) cells[col.id] = v;
      });
      return { ...row, cells };
    }),
  };
  const task = pick('ex', [(p.rubric.task_activities ?? []).map((a) => `▪ ${a}`).join('\n'), p.rubric.task].filter((t) => t.trim()).join('\n\n'));
  if (task) doc = { ...doc, exampleAnswer: { ...doc.exampleAnswer, example: task } };
  return doc;
}

/* ───────────── 평가지 ───────────── */

const PAGE_W = SHEET_CODE_DESIGN_PAGE_WIDTH_PX; // 794
const PAGE_H = Math.round((PAGE_W * 297) / 210); // 1123
const MARGIN_X = 0.06;
const CONTENT_W = 1 - MARGIN_X * 2;
const GAP_PX = 12;
const BOTTOM_PX = 40;
const FONT = "'Pretendard', 'Noto Sans KR', sans-serif";

/** 화면 밖에서 실제 높이 측정 (브라우저 전용, 실패 시 추정) */
export function measureHeightPx(opts: { text?: string; html?: string; widthPx: number; fontSizePx: number; bold?: boolean; lineHeight?: number }): number {
  const lh = opts.lineHeight ?? 1.45;
  try {
    const el = document.createElement('div');
    el.style.cssText = `position:fixed;left:-10000px;top:0;visibility:hidden;box-sizing:border-box;padding:6px 8px;width:${opts.widthPx}px;font-family:${FONT};font-size:${opts.fontSizePx}px;line-height:${lh};font-weight:${opts.bold ? 700 : 400};white-space:pre-wrap;word-break:keep-all;overflow-wrap:anywhere;`;
    if (opts.html != null) {
      el.style.whiteSpace = 'normal';
      el.style.padding = '0';
      el.innerHTML = opts.html;
    } else el.textContent = opts.text ?? '';
    document.body.appendChild(el);
    const h = el.scrollHeight;
    el.remove();
    if (h > 0) return h;
  } catch {
    /* fallthrough */
  }
  const perLine = Math.max(8, Math.floor((opts.widthPx - 16) / opts.fontSizePx));
  const lines = String(opts.text ?? '').split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(Array.from(l).length / perLine)), 0);
  return Math.ceil(lines * opts.fontSizePx * lh + 12);
}

export type SheetBuildResult = { document: EvaluationPlanSheetCanvasDocumentV1; pageCount: number; overflow: boolean };

type Blocks = {
  boxes: ProgramSlideTextBoxV1[];
  tables: ProgramSlideTableBlockV1[];
  shapes: ProgramSlideShapeBlockV1[];
  polylines: ProgramSlidePolylineBlockV1[];
  webpages: ProgramSlideWebpageBlockV1[];
};
const emptyBlocks = (): Blocks => ({ boxes: [], tables: [], shapes: [], polylines: [], webpages: [] });

/** 배치 단위: 높이(px)와 그리기 함수. 표는 행 단위로 쪼갤 수 있음 */
type Item = {
  h: number;
  /** 바로 다음 항목과 같은 쪽에 두기(제목·발문·조건 → 답란) */
  keepWithNext?: boolean;
  draw: (b: Blocks, yPx: number, z: () => number) => void;
  /** 남은 높이에 맞게 앞/뒤로 나누기(표 전용) */
  split?: (maxH: number) => [Item, Item] | null;
};

type Part = { items: Item[] };
type Group = { parts: Part[] };

/**
 * AI 섹션 → 여러 A4 페이지.
 * 페이지 나눔 우선순위: ① 문항(question) 경계 ② 문항이 한 쪽보다 길면 하위 부분(part: (1)(2), 〈조건〉/답란) 경계
 * ③ 부분도 길면 항목 경계 ④ 표는 행 경계(머리글 행 반복). 텍스트 블럭 중간은 자르지 않음.
 */
export function buildSheetCanvasDocumentFromAi(sheet: AiEvalSheetResult): SheetBuildResult {
  const doc = createDefaultEvaluationPlanSheetCanvasDocument();
  const widthPx = PAGE_W * CONTENT_W;
  const firstTopPx = PAGE_H * (doc.headerBandNorm || EVALUATION_PLAN_SHEET_HEADER_BAND_NORM) + 14;
  const nextTopPx = PAGE_H * EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM;
  const bottomPx = PAGE_H - BOTTOM_PX;

  const textItem = (text: string, fontSizePx: number, bold: boolean, align: ProgramSlideTextBoxV1['textAlign'] = 'left', keepWithNext = false): Item => {
    const h = measureHeightPx({ text, widthPx, fontSizePx, bold });
    return {
      h,
      keepWithNext,
      draw: (b, y, z) =>
        b.boxes.push({
          id: newId(), x: MARGIN_X, y: y / PAGE_H, w: CONTENT_W, h: h / PAGE_H, text, fontSizePx,
          colorHex: '#111111', bold, textAlign: align, lineHeight: 1.45, zIndex: z(),
        }),
    };
  };

  const tableItem = (rows: string[][], hasHeader: boolean): Item => {
    const colCount = Math.max(1, ...rows.map((r) => r.length));
    const colW = widthPx / colCount;
    const rowHs = rows.map((r, ri) =>
      Math.max(34, ...r.map((c) => measureHeightPx({ text: c, widthPx: colW, fontSizePx: 14, bold: hasHeader && ri === 0 })))
    );
    const total = rowHs.reduce((a, c) => a + c, 0);
    return {
      h: total,
      draw: (b, y, z) => {
        const cells: ProgramSlideTableCellV1[] = [];
        rows.forEach((r, ri) => {
          for (let ci = 0; ci < colCount; ci += 1) {
            const head = hasHeader && ri === 0;
            cells.push({
              id: newId(), text: r[ci] ?? '', textAlign: head ? 'center' : 'left', verticalAlign: 'middle',
              colorHex: '#111111', backgroundColorHex: head ? '#f1f3f5' : '#ffffff', bold: head, rowSpan: 1, colSpan: 1,
            });
          }
        });
        b.tables.push({
          id: newId(), x: MARGIN_X, y: y / PAGE_H, w: CONTENT_W, h: total / PAGE_H, rowCount: rows.length, colCount, cells,
          rowHeightsFrac: rowHs.map((h) => h / total), colWidthsFrac: Array.from({ length: colCount }, () => 1 / colCount),
          borderColorHex: '#333333', borderWidthPx: 1, defaultFontSizePx: 14, headerRow: hasHeader, zIndex: z(),
        });
      },
      split: (maxH) => {
        const head = hasHeader ? rows[0] : null;
        let acc = 0;
        let cut = -1;
        for (let i = 0; i < rows.length; i += 1) {
          if (acc + rowHs[i] > maxH) break;
          acc += rowHs[i];
          cut = i;
        }
        const minCut = hasHeader ? 1 : 0; // 머리글만 남기는 분할은 의미 없음
        if (cut < minCut || cut >= rows.length - 1) return null;
        const first = rows.slice(0, cut + 1);
        const rest = rows.slice(cut + 1);
        return [tableItem(first, hasHeader), tableItem(head ? [head, ...rest] : rest, hasHeader)];
      },
    };
  };

  /** 답란: 글쓰기 블럭(줄 수 = 높이/줄간격 → 블럭 크기만 맞추면 됨) */
  const answerItem = (lines: number, style: 'lines' | 'grid' | 'dots' | 'draw' | 'short' = 'lines'): Item => {
    const lineGap = 30;
    if (style === 'short') {
      // 단답: 한 줄 네모 칸(줄 없음)
      const h = 40;
      return {
        h,
        draw: (b, y0, z) =>
          b.shapes.push({
            id: newId(), x: MARGIN_X, y: y0 / PAGE_H, w: CONTENT_W * 0.5, h: h / PAGE_H, shape: 'rect', fillColorHex: '',
            strokeColorHex: '#9aa1a8', strokeWidthPx: 1, cornerRadiusPx: 8, text: '', fontSizePx: 14, colorHex: '#111111',
            bold: false, zIndex: z(),
          }),
      };
    }
    const minLines = style === 'lines' ? 2 : 6;
    const n = Math.max(minLines, Math.min(18, lines || minLines));
    const h = n * lineGap + 10;
    return {
      h,
      draw: (b, y0, z) => {
        b.shapes.push({
          id: newId(), x: MARGIN_X, y: y0 / PAGE_H, w: CONTENT_W, h: h / PAGE_H, shape: 'ruled', fillColorHex: '',
          strokeColorHex: '#9aa1a8', strokeWidthPx: 1, cornerRadiusPx: 8, text: '', fontSizePx: 14, colorHex: '#111111',
          bold: false, zIndex: z(), ruledLineSpacingPx: style === 'grid' ? 20 : lineGap, ruledLineColorHex: style === 'dots' ? '#9aa1a8' : style === 'draw' ? '#d6dadf' : style === 'grid' ? '#c8c8c8' : '#c8c8c8',
          ruledLineDashed: false,
          ...(style === 'lines' ? {} : { ruledBackground: style === 'draw' ? ('dots' as const) : style }),
        });
      },
    };
  };

  const htmlItem = (clean: string): Item => {
    const h = Math.max(40, measureHeightPx({ html: clean, widthPx, fontSizePx: 16, lineHeight: 1.5 }));
    return {
      h,
      draw: (b, y, z) =>
        b.webpages.push({
          id: newId(), x: MARGIN_X, y: y / PAGE_H, w: CONTENT_W, h: h / PAGE_H, mode: 'code', url: '', title: '',
          thumbSrc: '', sourceCode: clean, codeTransparentBackground: true, zIndex: z(),
        }),
    };
  };

  const sectionItems = (sec: AiEvalSheetSection): Item[] => {
    if (sec.type === 'heading') return [textItem(sec.text, 18, true, 'left', true)];
    if (sec.type === 'text') return [textItem(sec.text, 15, false, 'left', sec.question > 0)];
    if (sec.type === 'table' && sec.rows.length) {
      const out: Item[] = [];
      if (sec.text.trim()) out.push(textItem(sec.text, 15, true, 'left', true));
      out.push(tableItem(sec.rows, sec.rows.length > 1));
      return out;
    }
    if (sec.type === 'answerBox') {
      const out: Item[] = [];
      if (sec.text.trim()) out.push(textItem(sec.text, 14, false, 'left', true));
      out.push(answerItem(sec.lines, sec.answer_style || 'lines'));
      return out;
    }
    if (sec.type === 'html' && sec.html.trim()) {
      const clean = sanitizeSheetCodeHtml(sec.html);
      return clean.trim() ? [htmlItem(clean)] : [];
    }
    return [];
  };

  // 섹션 → 그룹(문항) → 부분
  const groups: Group[] = [];
  if (sheet.title.trim()) groups.push({ parts: [{ items: [textItem(sheet.title.trim(), 22, true, 'center')] }] });
  let curQ = -1;
  let curPart = -1;
  for (const sec of sheet.sections) {
    const items = sectionItems(sec);
    if (!items.length) continue;
    const q = sec.question > 0 ? sec.question : 0;
    if (q === 0 || q !== curQ) {
      groups.push({ parts: [{ items: [] }] });
      curQ = q === 0 ? -1 : q;
      curPart = sec.part;
    } else if (sec.part !== curPart) {
      groups[groups.length - 1].parts.push({ items: [] });
      curPart = sec.part;
    }
    const g = groups[groups.length - 1];
    g.parts[g.parts.length - 1].items.push(...items);
  }
  // 문항 아닌 단독 섹션이 제목성(keepWithNext)이면 다음 그룹과 합침
  for (let i = groups.length - 2; i >= 0; i -= 1) {
    const g = groups[i];
    const last = g.parts[g.parts.length - 1].items.slice(-1)[0];
    if (g.parts.length === 1 && g.parts[0].items.length === 1 && last?.keepWithNext) {
      groups[i + 1].parts[0].items.unshift(...g.parts[0].items);
      groups.splice(i, 1);
    }
  }

  const pages: Blocks[] = [emptyBlocks()];
  let page = 0;
  let y = firstTopPx;
  let zc = 1;
  const z = () => zc++;
  const top = () => (page === 0 ? firstTopPx : nextTopPx);
  const remaining = () => bottomPx - y;
  const capacity = () => bottomPx - nextTopPx;
  const newPage = () => {
    pages.push(emptyBlocks());
    page += 1;
    y = nextTopPx;
  };
  const atTop = () => Math.abs(y - top()) < 1;
  let overflow = false;
  const heightOf = (items: Item[]) => items.reduce((a, it) => a + it.h, 0) + Math.max(0, items.length - 1) * GAP_PX;
  const place = (it: Item) => {
    it.draw(pages[page], y, z);
    if (y + it.h > bottomPx + 1) overflow = true;
    y += it.h + GAP_PX;
  };
  const placeItems = (items: Item[]) => {
    // 항목 단위(keepWithNext 묶음 유지), 표는 행 단위 분할
    let i = 0;
    while (i < items.length) {
      let j = i;
      while (j < items.length - 1 && items[j].keepWithNext) j += 1;
      const chunk = items.slice(i, j + 1);
      const h = heightOf(chunk);
      if (h > remaining() && !atTop() && h <= capacity()) newPage();
      for (let k = 0; k < chunk.length; k += 1) {
        let it: Item | null = chunk[k];
        while (it) {
          if (it.h <= remaining() || atTop() && !it.split) {
            place(it);
            it = null;
          } else if (it.split) {
            const parts = it.split(remaining());
            if (parts) {
              place(parts[0]);
              newPage();
              it = parts[1];
            } else if (!atTop()) newPage();
            else {
              place(it);
              it = null;
            }
          } else {
            newPage();
          }
        }
      }
      i = j + 1;
    }
  };
  for (const g of groups) {
    const all = g.parts.flatMap((p) => p.items);
    const gh = heightOf(all);
    if (gh <= remaining()) {
      all.forEach(place);
      continue;
    }
    if (gh <= capacity()) {
      if (!atTop()) newPage();
      all.forEach(place);
      continue;
    }
    // 한 쪽보다 긴 문항: 부분 경계에서 나눔
    for (const p of g.parts) {
      const ph = heightOf(p.items);
      if (ph <= remaining()) p.items.forEach(place);
      else if (ph <= capacity()) {
        if (!atTop()) newPage();
        p.items.forEach(place);
      } else placeItems(p.items);
    }
  }

  const layouts = pages.map((b, i) => {
    const base = createEmptySheetLayoutSubset();
    void i;
    return { ...base, ...b };
  });
  return {
    document: withSheetCanvasPages(doc, layouts),
    pageCount: layouts.length,
    overflow,
  };
}
